/**
 * Klien HTTP tipis untuk API KamusBari.
 *
 * Kontrak yang diikat file ini (docs/API_CONTRACT.md bagian "Auth" dan
 * "Endpoints publik"):
 *
 * - Semua request wajib `credentials: 'include'`, karena sesi disimpan di cookie
 *   `session` yang HttpOnly. Tanpa itu `/api/auth/me`, `/api/me/entries`, vote,
 *   dan comment akan selalu terlihat sebagai anonymously.
 * - `VITE_API_BASE` opsional. Default '' = same-origin, yaitu mekanismenya: proxy
 *   dev Vite (`/api` -> worker di 127.0.0.1:8787) dan juga cara deploy Pages.
 * - Error API selalu berbentuk `{ error: { code, message } }` dengan HTTP status
 *   yang sesuai. Kita parse bentuk itu dan lempar `ApiError` supaya halaman bisa
 *   menampilkan pesan jujur, bukan diam-diam merender data kosong.
 */

/** Base URL API. Tanpa garis akhir yang tidak perlu. */
export const API_BASE: string = (import.meta.env.VITE_API_BASE ?? '').replace(/\/+$/, '')

/** Bentuk error yang dipakai seluruh endpoint. */
export interface ApiErrorBody {
  error: {
    code: string
    message: string
  }
  /** Field tambahan opsional, mis. `existing_id` pada 409 duplikat. */
  [extra: string]: unknown
}

/** Error yang dilempar helper ini kalau server membalas non-2xx. */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  /** Tubuh respons apa adanya, supaya field tambahan (mis. `existing_id`) terbaca. */
  readonly body: unknown

  constructor(status: number, code: string, message: string, body: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.body = body
  }

  /** 401 = sesi hilang/expired; 403 = butuh peran lain; 429 = kena rate limit. */
  get isAuth(): boolean {
    return this.status === 401 || this.status === 403
  }

  /** Pesan yang aman ditampilkan ke pengguna. */
  get displayMessage(): string {
    return this.message
  }
}

/** Error jaringan / abort, dibedakan dari ApiError supaya UI bisa worded jujur. */
export class NetworkError extends Error {
  constructor(message: string, readonly cause?: unknown) {
    super(message)
    this.name = 'NetworkError'
  }
}

/* ------------------------------------------------------------------ *
 * Tipe respons (ikut nama kolom di API_CONTRACT.md / skema D1)
 * ------------------------------------------------------------------ */

/** Satu entri dengan kolom ringkas — dipakai daftar di halaman bahasa/daerah. */
export interface EntryLite {
  id: string
  word: string
  meaning: string
  phonetic: string | null
  local_script: string | null
}

/** `entryFull` = semua kolom tabel `entries` + kolom gabungan dari join. */
export interface EntryFull extends EntryLite {
  local_script: string | null
  example: string | null
  etymology: string | null
  /** Disimpan sebagai JSON array di D1. */
  variants: string[] | string | null
  lang_id: string | null
  region_id: string | null
  source: string
  audio_url: string | null
  status: string
  upvotes: number
  author_id: string | null
  created_at: string
  updated_at: string
  /** Slug topik belajar; '' = belum dikategorikan (lihat migrations/0002). */
  topic: string
  /** Kelas kata Bahasa Indonesia; '' = belum diisi, jadi UI menyembunyikannya. */
  pos: string
  lang_slug: string | null
  lang_name: string | null
  region_slug: string | null
  province: string | null
  author_name: string | null
  comment_count: number
  /** 1 kalau viewer sudah memfavorit; hanya diisi endpoint yang tahu viewer. */
  is_favorite?: number
}

export interface Language {
  id: string
  slug: string
  name: string
  native_name: string | null
  region: string | null
  script: string | null
  /** slug cover/ikon; frontend memilih gambar dari slug ini. '' = fallback. */
  cover?: string
  entry_count: number
  /**
   * Kolom `description` ada di skema D1 tapi tidak disebut di daftar endpoint
   * `/api/languages`, jadi opsional: UI harus aman kalau tidak dikirim.
   */
  description?: string | null
  /**
   * Hanya terkirim untuk bahasa hasil pengajuan yang masih `pending`, dan itu
   * hanya ke Pengirimnya sendiri (lihat `GET /api/languages`). Bahasa approved
   * tidak pernah membawa nilai ini.
   */
  status?: 'approved' | 'pending'
}

export interface Region {
  id: string
  slug: string
  province: string
  island: string | null
  entry_count: number
}

export interface TopLanguage {
  slug: string
  name: string
  count: number
}

export interface Stats {
  totalEntries: number
  totalLanguages: number
  totalRegions: number
  totalContributors: number
  entriesThisWeek: number
  topLanguages: TopLanguage[]
}

export interface Paginated<T> {
  entries: T[]
  page: number
  limit: number
  total: number
  pages: number
}

export interface Comment {
  id: string
  body: string
  display_name: string
  created_at: string
}

export interface Suggestion {
  word: string
  slug: string
  lang: string
}

export interface User {
  id: string
  email: string
  display_name: string
  role: string
}

export interface LanguageDetail {
  language: Language
  entries: EntryLite[]
}

export interface EntryDetailResponse {
  entry: EntryFull
  comments: Comment[]
}

export interface EntriesQuery {
  q?: string
  lang?: string
  region?: string
  /** slug topik belajar (lihat TOPICS di lib/design.ts). */
  topic?: string
  sort?: 'popular' | 'newest' | 'az'
  page?: number
  limit?: number
}

/** Satu topik belajar + jumlah entri di dalamnya. */
export interface TopicCount {
  slug: string
  label: string
  count: number
}

/** Angka untuk kartu "Profil Saya". */
export interface UserStats {
  contributions: number
  languagesAdded: number
  favorites: number
}

/** Satu soal kuis pilihan ganda. */
export interface QuizQuestion {
  word: string
  meaning: string
  /** Pilihan arti; `answerIndex` menunjuk yang benar. */
  options: string[]
  answerIndex: number
  example: string
}

/* ------------------------------------------------------------------ *
 * Helper fetch
 * ------------------------------------------------------------------ */

type Method = 'GET' | 'POST' | 'PATCH' | 'DELETE'

/** Rakit URL API dari path relatif terhadap kontrak (`/entries`, `/auth/me`, ...). */
export function apiUrl(path: string, query?: Record<string, string | number | undefined>): string {
  const clean = path.startsWith('/') ? path : `/${path}`
  const search = new URLSearchParams()
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === '') continue
      search.set(key, String(value))
    }
  }
  const qs = search.toString()
  return `${API_BASE}/api${clean}${qs ? `?${qs}` : ''}`
}

async function parseError(res: Response): Promise<ApiError> {
  let body: unknown = null
  let message = `Permintaan gagal (HTTP ${res.status}).`
  let code = `http_${res.status}`
  try {
    const text = await res.text()
    if (text) {
      body = JSON.parse(text) as unknown
      const err = (body as Partial<ApiErrorBody> | null)?.error
      if (err && typeof err === 'object') {
        if (typeof err.code === 'string') code = err.code
        if (typeof err.message === 'string' && err.message) message = err.message
      }
    }
  } catch {
    // Respons bukan JSON (mis. halaman error dari proxy). Pesan generik di atas
    // sudah jujur; jangan pretending body-nya terbaca.
  }
  return new ApiError(res.status, code, message, body)
}

interface RequestOptions {
  method?: Method
  /** Badan request; otomatis di-JSON-kan. */
  body?: unknown
  /** Query string; nilai `undefined` dan string kosong dibuang. */
  query?: Record<string, string | number | undefined>
  signal?: AbortSignal
}

/**
 * Satu-satunya titik keluar ke jaringan. Semua fetch di app wajib lewat sini
 * supaya `credentials: 'include'` dan bentuk error tidak pernah terlupa.
 */
export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  // apiUrl() sudah menambah prefix /api. Path yang ditulis lengkap di sini
  // berakhir jadi /api/api/... dan server membalas 404 yang tidak mengarah ke
  // penyebabnya. Bug ini sudah terjadi dua kali, jadi di sini dicegah - lebih
  // baik gagal cepat dengan pesan yang menyebut path-nya daripada 404 yang
  // membuat orang menelusuri hal yang salah.
  if (path.startsWith('/api/')) {
    throw new ApiError(
      0,
      'invalid_path',
      `apiFetch path tidak boleh diawali "/api": ${path}. Gunakan "${path.replace(/^\/api/, '')}".`,
      null,
    )
  }
  const { method = 'GET', body, query, signal } = options
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  let res: Response
  try {
    res = await fetch(apiUrl(path, query), {
      method,
      headers,
      // Wajib: sesi ada di cookie HttpOnly.
      credentials: 'include',
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    })
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err
    throw new NetworkError('Tidak bisa menghubungi server. Periksa koneksi Anda lalu coba lagi.', err)
  }

  if (!res.ok) throw await parseError(res)
  if (res.status === 204) return undefined as T

  const text = await res.text()
  if (!text) return undefined as T
  try {
    return JSON.parse(text) as T
  } catch {
    throw new NetworkError('Respons server tidak bisa dibaca (bukan JSON).')
  }
}

/** Versi GET dengan query string. Nilai kosong/`undefined` dibuang dari query. */
export function apiGet<T>(
  path: string,
  query?: Record<string, string | number | undefined>,
  signal?: AbortSignal,
): Promise<T> {
  return apiFetch<T>(path, { method: 'GET', query, signal })
}

/** POST JSON. Dipakai auth, submit entri, vote, dan comment. */
export function apiPost<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  return apiFetch<T>(path, { method: 'POST', body: body ?? {}, signal })
}

/* ------------------------------------------------------------------ *
 * Endpoint publik
 * ------------------------------------------------------------------ */

export function getHealth(signal?: AbortSignal) {
  return apiFetch<{ ok: boolean; time: string }>('/health', { signal })
}

export async function getLanguages(signal?: AbortSignal) {
  const res = await apiFetch<{ languages: Language[] }>('/languages', { signal })
  return res.languages ?? []
}

export function getLanguage(slug: string, signal?: AbortSignal) {
  return apiFetch<LanguageDetail>(`/languages/${encodeURIComponent(slug)}`, { signal })
}

export async function getRegions(signal?: AbortSignal) {
  const res = await apiFetch<{ regions: Region[] }>('/regions', { signal })
  return res.regions ?? []
}

export async function getStats(signal?: AbortSignal) {
  // API membalut hasil dalam { stats: {...} }. Tanpa unwrap di sini, field-nya
  // jadi undefined dan kartu statistik merender 0 padahal datanya ada.
  const res = await apiFetch<{ stats: Stats }>('/stats', { signal })
  return res.stats
}

export function getEntries(query: EntriesQuery = {}, signal?: AbortSignal) {
  const q: Record<string, string | number | undefined> = {
    q: query.q,
    lang: query.lang,
    region: query.region,
    topic: query.topic,
    sort: query.sort,
    page: query.page,
    // Kontrak: limit <= 50.
    limit: query.limit === undefined ? undefined : Math.min(Math.max(query.limit, 1), 50),
  }
  return apiFetch<Paginated<EntryFull>>('/entries', { method: 'GET', query: q, signal })
}

export function getEntry(id: string, signal?: AbortSignal) {
  return apiFetch<EntryDetailResponse>(`/entries/${encodeURIComponent(id)}`, { signal })
}

export async function getRelated(id: string, signal?: AbortSignal) {
  const res = await apiFetch<{ entries: EntryLite[] }>(`/entries/${encodeURIComponent(id)}/related`, {
    signal,
  })
  return res.entries ?? []
}

export async function getSuggest(q: string, signal?: AbortSignal) {
  const res = await apiFetch<{ suggest: Suggestion[] }>('/search/suggest', {
    method: 'GET',
    query: { q },
    signal,
  })
  return res.suggest ?? []
}

/* ------------------------------------------------------------------ *
 * Endpoint yang butuh sesi (cookie HttpOnly -> selalu credentials: 'include')
 * ------------------------------------------------------------------ */

export function register(payload: { email: string; password: string; displayName: string }) {
  return apiPost<{ user: User }>('/auth/register', payload)
}

export function login(payload: { email: string; password: string }) {
  return apiPost<{ user: User }>('/auth/login', payload)
}

export function logout() {
  return apiPost<void>('/auth/logout')
}

export async function me(signal?: AbortSignal) {
  const res = await apiFetch<{ user: User | null }>('/auth/me', { signal })
  return res.user ?? null
}

export async function getMyEntries(signal?: AbortSignal) {
  const res = await apiFetch<{ entries: EntryFull[] }>('/me/entries', { signal })
  return res.entries ?? []
}

export interface NewEntryPayload {
  word: string
  meaning: string
  example?: string
  phonetic?: string
  local_script?: string
  lang_slug: string
  region_slug?: string
  etymology?: string
  variants?: string[]
  source: string
  audio_url?: string
  /** slug topik belajar; kosong = belum dikategorikan. */
  topic?: string
  /** kelas kata; kosong = belum diisi. */
  pos?: string
}

export function createEntry(payload: NewEntryPayload) {
  return apiPost<{ entry: EntryFull }>('/entries', payload)
}

/* ------------------------------------------------------------------ *
 * Pengajuan bahasa baru (lihat migrations/0003)
 * ------------------------------------------------------------------ */

export interface NewLanguagePayload {
  /** Nama bahasa dalam bahasa Indonesia, mis. "Bahasa Sulu". */
  name: string
  /** Nama Writes itself, mis. "Sinūggang Sinūggang". */
  native_name?: string
  /** Daerah asal bebas teks: "Ternate, Maluku Utara". */
  region?: string
  /** Sistem tulisan: "Latin", "Aksara Bali", "Aksara Jawa". */
  script?: string
  /** Penjangan singkat; server mewajibkan minimal 10 karakter. */
  description: string
}

export interface LanguageSubmission {
  id: string
  name: string
  native_name: string
  region: string
  description: string
  created_at: string
  decision: 'pending' | 'approved' | 'rejected'
  /** Terisi kalau bahasa sudah dibuat; null kalau pengajuan ditolak. */
  slug: string | null
}

/**
 * Ajukan bahasa daerah yang belum ada di kamus.
 *
 * Server selalu mengembalikan status `pending`: bahasa tidak langsung tayang,
 * tapi langsung bisa dipakai menambah kata oleh yang mengajukan.
 * 409 = nama sudah ada (di kamus, atau pengajuan milik sendiri yang sama).
 */
export function createLanguage(payload: NewLanguagePayload) {
  return apiPost<{
    language: { slug: string | null; name: string; status: 'pending' }
    duplicate?: boolean
    message: string
  }>('/languages', payload)
}

/** Riwayat pengajuan bahasa milik user yang sedang masuk. */
export function fetchMyLanguageSubmissions() {
  return apiGet<{ submissions: LanguageSubmission[] }>('/me/languages')
}

/* ------------------------------------------------------------------ *
 * Moderasi bahasa (admin) — /api/moderation/language-submissions
 * ------------------------------------------------------------------ */

export interface AdminLanguageSubmission extends LanguageSubmission {
  /** Nama akun pengaju, untuk moderator menilai. */
  author_name: string | null
  /** Berapa entri yang sudah menunggu di bawah bahasa ini. */
  pending_entries: number
  decision_note: string
  /** Sistem tulisan; default server 'Latin' kalau pengaju mengosongkan. */
  script: string
  /** Null kalau baris `languages` sudah tidak ada (mis. pengajuan ditolak). */
  slug: string | null
}

/** Antrean bahasa yang menunggu keputusan. Admin saja (403 untuk selainnya). */
export function fetchLanguageModerationQueue() {
  return apiGet<{ submissions: AdminLanguageSubmission[] }>(
    '/moderation/language-submissions/queue',
  )
}

/**
 * Setujui bahasa: langsung tayang untuk semua orang.
 *
 * `slug` ikut dikirim, bukan cuma id pengajuan, supaya server bisa tahu baris
 * bahasa mana yang harus diubah statusnya tanpa menebak dari nama.
 */
export function approveLanguageSubmission(sub: { id: string; slug: string | null }) {
  return apiPost<{ submission: AdminLanguageSubmission }>(
    `/moderation/language-submissions/${encodeURIComponent(sub.id)}/approve`,
    { slug: sub.slug },
  )
}

/** Tolak bahasa. `reason` wajib dan disimpan sebagai jejak audit. */
export function rejectLanguageSubmission(sub: { id: string; slug: string | null }, reason: string) {
  return apiPost<{ submission: AdminLanguageSubmission }>(
    `/moderation/language-submissions/${encodeURIComponent(sub.id)}/reject`,
    { reason },
  )
}

export function voteEntry(id: string) {
  return apiPost<{ upvotes: number; voted: boolean }>(`/entries/${encodeURIComponent(id)}/vote`)
}

export function commentOnEntry(id: string, body: string) {
  return apiPost<{ comment: Comment }>(`/entries/${encodeURIComponent(id)}/comments`, { body })
}

/* ------------------------------------------------------------------ *
 * Favorit, statistik, topik, kuis
 * ------------------------------------------------------------------ */

/** Tandai entri sebagai favorit. Tanpa argumen nilainya ditoggle di server. */
export function toggleEntryFavorite(id: string, favorited?: boolean) {
  return apiPost<{ favorited: boolean }>(`/entries/${encodeURIComponent(id)}/favorite`, {
    favorited,
  })
}

/** Tandai bahasa sebagai favorit. Tanpa argumen nilainya ditoggle di server. */
export function toggleLanguageFavorite(slug: string, favorited?: boolean) {
  return apiPost<{ favorited: boolean }>(`/languages/${encodeURIComponent(slug)}/favorite`, {
    favorited,
  })
}

export async function getFavoriteEntries(signal?: AbortSignal) {
  const res = await apiFetch<{ entries: EntryFull[] }>('/me/favorites', {
    method: 'GET',
    query: { type: 'entry' },
    signal,
  })
  return res.entries ?? []
}

export async function getFavoriteLanguages(signal?: AbortSignal) {
  const res = await apiFetch<{ languages: Language[] }>('/me/favorites', {
    method: 'GET',
    query: { type: 'lang' },
    signal,
  })
  return res.languages ?? []
}

export async function getMyStats(signal?: AbortSignal) {
  const res = await apiFetch<{ stats: UserStats }>('/me/stats', { method: 'GET', signal })
  return res.stats
}

/** Delapan topik belajar + jumlah entri approved untuk sebuah bahasa. */
export async function getLanguageTopics(slug: string, signal?: AbortSignal) {
  const res = await apiFetch<{ topics: TopicCount[] }>(
    `/languages/${encodeURIComponent(slug)}/topics`,
    { method: 'GET', signal },
  )
  return res.topics ?? []
}

/** Kuis pilihan ganda untuk sebuah bahasa, opsional dibatasi satu topik. */
export async function getQuiz(
  slug: string,
  options: { topic?: string; limit?: number } = {},
  signal?: AbortSignal,
): Promise<QuizQuestion[]> {
  const res = await apiFetch<{ questions: QuizQuestion[] }>(
    `/languages/${encodeURIComponent(slug)}/quiz`,
    { method: 'GET', query: { topic: options.topic, limit: options.limit }, signal },
  )
  return res.questions ?? []
}

/* ------------------------------------------------------------------ *
 * Utilitas tampilan
 * ------------------------------------------------------------------ */

/**
 * `variants` disimpan sebagai JSON array di D1, tapi entri crowdsourced /
 * seed bisa kirim bentuk lain. Normalisasi ke array string supaya UI tidak
 * pernah menampilkan "[object Object]".
 */
export function toVariantList(value: EntryFull['variants']): string[] {
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string')
  if (typeof value !== 'string' || !value.trim()) return []
  try {
    const parsed: unknown = JSON.parse(value)
    if (Array.isArray(parsed)) return parsed.filter((v): v is string => typeof v === 'string')
  } catch {
    // Bukan JSON: anggap satu string yang dipisah koma.
  }
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

/** Pesan error yang aman ditampilkan ke pengguna dari error apa pun. */
export function errorMessage(err: unknown, fallback = 'Terjadi kesalahan. Silakan coba lagi.'): string {
  if (err instanceof ApiError) return err.displayMessage
  if (err instanceof NetworkError) return err.message
  if (err instanceof Error && err.message) return err.message
  return fallback
}
